import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Snackbar,
  useMediaQuery,
  useTheme,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";

const TEMPLATE_TYPE_LABELS = {
  confirmation: "Potwierdzenie zapisu",
  cancellation: "Odwołanie zajęć",
  payment_timeout: "Anulowanie — brak płatności",
  payment_reminder: "Przypomnienie o płatności",
  time_change: "Zmiana godziny",
  lesson_update: "Zmiana szczegółów zajęć",
};

const parseVariables = (vars) => {
  if (Array.isArray(vars)) return vars;
  if (typeof vars === "string") {
    try { return JSON.parse(vars); } catch { return []; }
  }
  return [];
};

const EmailTemplateManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [templates, setTemplates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Edit template dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [saving, setSaving] = useState(false);

  // Override dialog
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [overrideLocationId, setOverrideLocationId] = useState("");
  const [overrideSubject, setOverrideSubject] = useState("");
  const [overrideBody, setOverrideBody] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);
  const [isNewOverride, setIsNewOverride] = useState(false);

  // Delete override dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingOverrideId, setDeletingOverrideId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: [] }, { background: [] }],
        ["link"],
        ["clean"],
      ],
    }),
    []
  );

  const formats = [
    "header", "bold", "italic", "underline", "strike",
    "list", "bullet", "color", "background", "link",
  ];

  useEffect(() => {
    fetchTemplates();
    fetchLocations();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    setError("");
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/email-templates/`,
        { headers }
      );
      if (response.status === 200) {
        setTemplates(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch email templates", err);
      setError("Nie udało się załadować szablonów e-mail");
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type-locations/`,
        { headers }
      );
      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : response.data.objects || [];
        setLocations(data);
      }
    } catch (err) {
      console.error("Failed to fetch locations", err);
    }
  };

  const handleEditClick = async (template) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/email-templates/${template.template_type}`,
        { headers }
      );
      if (response.status === 200) {
        setEditingTemplate(response.data);
        setEditSubject(response.data.subject);
        setEditBody(response.data.body);
        setEditDialogOpen(true);
      }
    } catch (err) {
      console.error("Failed to fetch template details", err);
      showSnackbar("Nie udało się załadować szablonu", "error");
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/email-templates/${editingTemplate.template_type}`,
        { subject: editSubject, body: editBody },
        { headers }
      );
      if (response.status === 200) {
        setEditingTemplate(response.data);
        showSnackbar("Szablon został zapisany", "success");
        fetchTemplates();
      }
    } catch (err) {
      console.error("Failed to save template", err);
      showSnackbar("Nie udało się zapisać szablonu", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddOverride = () => {
    setIsNewOverride(true);
    setEditingOverride(null);
    setOverrideLocationId("");
    setOverrideSubject(editingTemplate?.subject || "");
    setOverrideBody(editingTemplate?.body || "");
    setOverrideDialogOpen(true);
  };

  const handleEditOverride = (override) => {
    setIsNewOverride(false);
    setEditingOverride(override);
    setOverrideLocationId(override.location_id);
    setOverrideSubject(override.subject || editingTemplate?.subject || "");
    setOverrideBody(override.body || editingTemplate?.body || "");
    setOverrideDialogOpen(true);
  };

  const handleSaveOverride = async () => {
    if (!editingTemplate) return;
    setSavingOverride(true);
    try {
      const headers = await getAuthHeaders();
      let response;

      if (isNewOverride) {
        response = await axios.post(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/email-templates/${editingTemplate.template_type}/overrides`,
          {
            location_id: overrideLocationId,
            subject: overrideSubject,
            body: overrideBody,
          },
          { headers }
        );
      } else {
        response = await axios.put(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/email-templates/overrides/${editingOverride.id}`,
          {
            subject: overrideSubject,
            body: overrideBody,
          },
          { headers }
        );
      }

      if (response.status === 200) {
        setEditingTemplate(response.data);
        setOverrideDialogOpen(false);
        showSnackbar(
          isNewOverride ? "Dodano wersję lokalizacyjną" : "Zaktualizowano wersję lokalizacyjną",
          "success"
        );
        fetchTemplates();
      }
    } catch (err) {
      console.error("Failed to save override", err);
      const detail = err.response?.data?.detail || "Nie udało się zapisać";
      showSnackbar(detail, "error");
    } finally {
      setSavingOverride(false);
    }
  };

  const handleDeleteOverride = async () => {
    if (!deletingOverrideId) return;
    setDeleting(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/email-templates/overrides/${deletingOverrideId}`,
        { headers }
      );
      if (response.status === 200) {
        setEditingTemplate(response.data);
        setDeleteDialogOpen(false);
        setDeletingOverrideId(null);
        showSnackbar("Usunięto wersję lokalizacyjną", "success");
        fetchTemplates();
      }
    } catch (err) {
      console.error("Failed to delete override", err);
      showSnackbar("Nie udało się usunąć", "error");
    } finally {
      setDeleting(false);
    }
  };

  const insertVariable = (variable, setter) => {
    setter((prev) => prev + `{{${variable}}}`);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const usedLocationIds = editingTemplate?.overrides?.map((o) => o.location_id) || [];
  const availableLocationsForOverride = locations.filter(
    (l) => !usedLocationIds.includes(l.id)
  );

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
            <EmailIcon color="primary" />
            Szablony E-mail
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Zarządzaj treścią automatycznych wiadomości e-mail. Możesz tworzyć wersje lokalizacyjne dla każdego studia.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchTemplates} disabled={loading}>
          Odśwież
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Paper sx={{ overflow: "hidden" }}>
          <TableContainer>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Typ szablonu</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Temat</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Wersje lokalizacyjne</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((tpl) => (
                  <TableRow key={tpl.template_type} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {TEMPLATE_TYPE_LABELS[tpl.template_type] || tpl.template_type}
                      </Typography>
                      {tpl.description && (
                        <Typography variant="caption" color="text.secondary">
                          {tpl.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {tpl.subject}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        icon={<LocationIcon fontSize="small" />}
                        label={tpl.override_count || 0}
                        color={tpl.override_count > 0 ? "primary" : "default"}
                        variant={tpl.override_count > 0 ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="primary" onClick={() => handleEditClick(tpl)} title="Edytuj szablon">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {templates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        Brak szablonów e-mail
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Edit Template Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EmailIcon color="primary" />
            Edytuj szablon: {TEMPLATE_TYPE_LABELS[editingTemplate?.template_type] || editingTemplate?.template_type}
          </Box>
          <IconButton size="small" onClick={() => setEditDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editingTemplate && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Available Variables */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Dostępne zmienne (kliknij aby wstawić do tematu):
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {parseVariables(editingTemplate.available_variables).map((v) => (
                    <Tooltip key={v} title={`Wstaw {{${v}}} do tematu`}>
                      <Chip
                        size="small"
                        label={`{{${v}}}`}
                        color="info"
                        variant="outlined"
                        clickable
                        onClick={() => insertVariable(v, setEditSubject)}
                        icon={<CopyIcon fontSize="small" />}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Box>

              {/* Subject */}
              <TextField
                label="Temat e-mail"
                fullWidth
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
                helperText="Możesz używać zmiennych np. {{lesson_name}}"
              />

              {/* Body Editor */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Treść e-mail (HTML):
                </Typography>
                <ReactQuill
                  value={editBody}
                  onChange={setEditBody}
                  modules={modules}
                  formats={formats}
                  style={{ minHeight: 250 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Używaj zmiennych w treści wpisując je ręcznie, np. {"{{user_first_name}}"} lub {"{{location_address}}"}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Location Overrides */}
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocationIcon color="secondary" />
                    Wersje lokalizacyjne
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddOverride}
                    disabled={availableLocationsForOverride.length === 0}
                  >
                    Dodaj wersję
                  </Button>
                </Box>

                {editingTemplate.overrides && editingTemplate.overrides.length > 0 ? (
                  <List dense>
                    {editingTemplate.overrides.map((override) => (
                      <ListItem
                        key={override.id}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <LocationIcon fontSize="small" color="secondary" />
                              <Typography variant="body2" fontWeight={500}>
                                {override.location_name || `Lokalizacja #${override.location_id}`}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            override.subject
                              ? `Temat: ${override.subject.substring(0, 60)}...`
                              : "Używa domyślnego tematu"
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton size="small" color="primary" onClick={() => handleEditOverride(override)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setDeletingOverrideId(override.id);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info" variant="outlined">
                    Brak wersji lokalizacyjnych. Wszystkie e-maile używają domyślnego szablonu.
                    {availableLocationsForOverride.length > 0 && ' Kliknij "Dodaj wersję" aby dodać odmianę dla konkretnej lokalizacji.'}
                  </Alert>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} startIcon={<CloseIcon />}>
            Zamknij
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
            disabled={saving || !editSubject.trim() || !editBody.trim()}
          >
            Zapisz szablon
          </Button>
        </DialogActions>
      </Dialog>

      {/* Override Edit/Create Dialog */}
      <Dialog
        open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationIcon color="secondary" />
            {isNewOverride ? "Dodaj wersję lokalizacyjną" : "Edytuj wersję lokalizacyjną"}
          </Box>
          <IconButton size="small" onClick={() => setOverrideDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
            {isNewOverride && (
              <FormControl fullWidth required>
                <InputLabel>Lokalizacja</InputLabel>
                <Select
                  value={overrideLocationId}
                  onChange={(e) => setOverrideLocationId(e.target.value)}
                  label="Lokalizacja"
                >
                  {availableLocationsForOverride.map((loc) => (
                    <MenuItem key={loc.id} value={loc.id}>
                      {loc.name} — {loc.address}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!isNewOverride && editingOverride && (
              <Alert severity="info" variant="outlined">
                Lokalizacja: <strong>{editingOverride.location_name}</strong>
              </Alert>
            )}

            {/* Available Variables */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Dostępne zmienne:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {parseVariables(editingTemplate?.available_variables).map((v) => (
                  <Chip
                    key={v}
                    size="small"
                    label={`{{${v}}}`}
                    color="info"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>

            <TextField
              label="Temat e-mail (zostaw pusty aby użyć domyślnego)"
              fullWidth
              value={overrideSubject}
              onChange={(e) => setOverrideSubject(e.target.value)}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Treść e-mail (zostaw pusty aby użyć domyślnej):
              </Typography>
              <ReactQuill
                value={overrideBody}
                onChange={setOverrideBody}
                modules={modules}
                formats={formats}
                style={{ minHeight: 250 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOverrideDialogOpen(false)} startIcon={<CloseIcon />}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveOverride}
            startIcon={savingOverride ? <CircularProgress size={18} /> : <SaveIcon />}
            disabled={savingOverride || (isNewOverride && !overrideLocationId)}
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Override Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DeleteIcon color="error" />
          Usuń wersję lokalizacyjną
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Czy na pewno chcesz usunąć tę wersję lokalizacyjną? E-maile dla tej lokalizacji będą używać domyślnego szablonu.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteOverride}
            startIcon={deleting ? <CircularProgress size={18} /> : <DeleteIcon />}
            disabled={deleting}
          >
            Usuń
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailTemplateManagement;
